import { useState, useEffect, useCallback, useRef } from "react";
import {
  Settings2, Check, GripVertical, ChevronDown, ChevronUp,
  ArrowUpDown, EyeOff, Replace, X, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Customize Columns Dropdown ----
export function ColumnCustomizer({ visibleColumns, onColumnsChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [systemCols, setSystemCols] = useState([]);
  const [customCols, setCustomCols] = useState([]);
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const panelRef = useRef(null);

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-table-preferences`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSystemCols(data.system_columns || []);
        setCustomCols(data.custom_columns || []);
        setSelected(data.columns || []);
        setOrder(data.column_order || data.columns || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) fetchPreferences();
  }, [open, fetchPreferences]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const allFields = [...systemCols, ...customCols];
  const fieldMap = {};
  allFields.forEach(f => { fieldMap[f.key] = f; });

  const toggleColumn = (key) => {
    setSelected(prev => {
      if (prev.includes(key)) {
        setOrder(o => o.filter(k => k !== key));
        return prev.filter(k => k !== key);
      }
      if (!prev.includes(key)) {
        setOrder(o => o.includes(key) ? o : [...o, key]);
        return [...prev, key];
      }
      return prev;
    });
  };

  const handleSave = async () => {
    try {
      // Ensure order only contains selected columns
      const finalOrder = order.filter(k => selected.includes(k));
      // Add any selected that aren't in order
      selected.forEach(k => { if (!finalOrder.includes(k)) finalOrder.push(k); });
      
      const res = await fetch(`${BACKEND_URL}/api/lead-table-preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ columns: selected, column_order: finalOrder })
      });
      if (res.ok) {
        toast.success("Column preferences saved");
        onColumnsChange(selected, finalOrder, allFields);
        setOpen(false);
      }
    } catch (e) { toast.error("Failed to save"); }
  };

  const handleReset = async () => {
    const defaults = ["name", "phone", "remark", "source", "stage", "assigned_to_name", "created_at"];
    setSelected(defaults);
    setOrder(defaults);
  };

  // Drag & drop reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newOrder = [...order.filter(k => selected.includes(k))];
    const item = newOrder[dragIdx];
    newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, item);
    setOrder(newOrder);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const orderedSelected = order.filter(k => selected.includes(k));

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className="gap-1.5"
        data-testid="customize-columns-btn"
      >
        <Settings2 className="w-4 h-4" />
        <span className="hidden sm:inline">Columns</span>
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden" data-testid="column-customizer-panel">
          <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-800">Customize Columns</p>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-indigo-500" />
            </div>
          ) : (
            <>
              {/* Active columns - reorderable */}
              <div className="px-3 py-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Active Columns (drag to reorder)</p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {orderedSelected.map((key, idx) => {
                    const f = fieldMap[key];
                    if (!f) return null;
                    return (
                      <div
                        key={key}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing transition-colors ${
                          dragIdx === idx ? "bg-indigo-50 border border-indigo-200" : "hover:bg-slate-50"
                        }`}
                        data-testid={`active-col-${key}`}
                      >
                        <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                        <span className="text-sm text-slate-700 flex-1">{f.label}</span>
                        <Badge className={`text-[9px] px-1 py-0 ${f.type === "custom" ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-500"}`}>
                          {f.type === "custom" ? "Custom" : "System"}
                        </Badge>
                        <button
                          onClick={() => toggleColumn(key)}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                          title="Remove column"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-t mx-3" />

              {/* Available columns */}
              <div className="px-3 py-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">System Fields</p>
                <div className="space-y-0.5 max-h-[140px] overflow-y-auto">
                  {systemCols.map(f => (
                    <label
                      key={f.key}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer"
                      data-testid={`sys-col-${f.key}`}
                    >
                      <Checkbox
                        checked={selected.includes(f.key)}
                        onCheckedChange={() => toggleColumn(f.key)}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-sm text-slate-600">{f.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {customCols.length > 0 && (
                <>
                  <div className="border-t mx-3" />
                  <div className="px-3 py-2">
                    <p className="text-[10px] font-semibold text-teal-500 uppercase tracking-wider mb-2">Custom Fields</p>
                    <div className="space-y-0.5 max-h-[120px] overflow-y-auto">
                      {customCols.map(f => (
                        <label
                          key={f.key}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-teal-50 cursor-pointer"
                          data-testid={`custom-col-${f.key}`}
                        >
                          <Checkbox
                            checked={selected.includes(f.key)}
                            onCheckedChange={() => toggleColumn(f.key)}
                            className="h-3.5 w-3.5"
                          />
                          <span className="text-sm text-teal-700">{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Footer */}
              <div className="px-3 py-3 bg-slate-50 border-t flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={handleReset} className="text-xs text-slate-500">
                  Reset Default
                </Button>
                <div className="flex-1" />
                <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="text-xs">Cancel</Button>
                <Button size="sm" onClick={handleSave} className="text-xs" data-testid="save-columns-btn">
                  <Check className="w-3.5 h-3.5 mr-1" /> Apply
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Column Header with Dropdown ----
export function SortableColumnHeader({ columnKey, label, sortable, sortColumn, sortDirection, onSort, onHide, allFields, onReplace }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setShowReplace(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const isActive = sortColumn === columnKey;

  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide relative" data-testid={`th-${columnKey}`}>
      <div className="flex items-center gap-1">
        <button
          onClick={() => {
            if (sortable) onSort(columnKey);
            else setMenuOpen(!menuOpen);
          }}
          className="flex items-center gap-1 hover:text-indigo-600 transition-colors"
        >
          {label}
          {isActive && (sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); setShowReplace(false); }}
          className="ml-0.5 text-slate-300 hover:text-slate-600 transition-colors"
          data-testid={`col-menu-${columnKey}`}
        >
          <ChevronDown className="w-3 h-3" />
        </button>
      </div>

      {menuOpen && (
        <div ref={menuRef} className="absolute left-0 top-full mt-1 w-52 bg-white rounded-lg border border-slate-200 shadow-lg z-50 py-1" data-testid={`col-dropdown-${columnKey}`}>
          {!showReplace ? (
            <>
              {sortable && (
                <>
                  <button
                    onClick={() => { onSort(columnKey); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    data-testid={`sort-asc-${columnKey}`}
                  >
                    <ChevronUp className="w-3.5 h-3.5" /> Sort A-Z
                  </button>
                  <button
                    onClick={() => { onSort(columnKey); onSort(columnKey); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    data-testid={`sort-desc-${columnKey}`}
                  >
                    <ChevronDown className="w-3.5 h-3.5" /> Sort Z-A
                  </button>
                  <div className="border-t my-1" />
                </>
              )}
              <button
                onClick={() => setShowReplace(true)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                data-testid={`replace-${columnKey}`}
              >
                <Replace className="w-3.5 h-3.5" /> Replace with...
              </button>
              <button
                onClick={() => { onHide(columnKey); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                data-testid={`hide-${columnKey}`}
              >
                <EyeOff className="w-3.5 h-3.5" /> Hide Column
              </button>
            </>
          ) : (
            <div className="max-h-[240px] overflow-y-auto">
              <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase">Replace with</p>
              {allFields.filter(f => f.key !== columnKey).map(f => (
                <button
                  key={f.key}
                  onClick={() => { onReplace(columnKey, f.key); setMenuOpen(false); setShowReplace(false); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-indigo-50"
                  data-testid={`replace-option-${f.key}`}
                >
                  <span className={`text-[9px] font-medium px-1 rounded ${f.type === "custom" ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-500"}`}>
                    {f.type === "custom" ? "C" : "S"}
                  </span>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </th>
  );
}
